// Il ritaglio del ritratto: la foto dentro un quadrato, con un cerchio al centro (il medaglione
// dell'albero). Un dito la sposta, due dita la ingrandiscono; − / + e le frecce fanno lo stesso.
// La foto copre sempre tutto il quadrato: non si può lasciare un bordo vuoto nel medaglione.
import { el } from './ui.js';

export type Ritaglio = { x: number; y: number; lato: number }; // frazioni: x e lato della larghezza, y dell'altezza

export function editorRitaglio(src: string, alt: string): { element: HTMLElement; pronto: Promise<void>; ritaglio: () => Ritaglio } {
  const img = el('img', { src, alt, draggable: 'false', class: 'ritaglio-foto' }) as HTMLImageElement;
  const cornice = el('div', { class: 'ritaglio-cornice', tabindex: '0', role: 'group', 'aria-label': `Sposta la foto col dito finché il volto è nel cerchio. Due dita per avvicinare. ${alt}` },
    img, el('div', { class: 'ritaglio-maschera', 'aria-hidden': 'true' }));
  let W = 1, H = 1, scala = 1, minimo = 1, tx = 0, ty = 0, S = 1;
  const applica = (): void => { img.style.transform = `translate(${tx}px, ${ty}px) scale(${scala})`; };
  const limita = (): void => {
    tx = Math.min(0, Math.max(S - W * scala, tx));
    ty = Math.min(0, Math.max(S - H * scala, ty));
  };
  const zoom = (s: number, cx = S / 2, cy = S / 2): void => {
    const nuova = Math.max(minimo, Math.min(minimo * 6, s));
    tx = cx - ((cx - tx) / scala) * nuova; ty = cy - ((cy - ty) / scala) * nuova; scala = nuova;
    limita(); applica();
  };
  const pronto = new Promise<void>((ok) => {
    const via = (): void => {
      W = img.naturalWidth || 1; H = img.naturalHeight || 1; S = cornice.clientWidth || 300;
      // Pixel veri: i calcoli partono da qui, e un foglio di stile esterno non deve ridimensionarla.
      img.style.width = `${W}px`; img.style.height = `${H}px`;
      // All'inizio: la foto copre il quadrato, centrata (spesso il volto è già lì).
      scala = minimo = S / Math.min(W, H);
      tx = (S - W * scala) / 2; ty = (S - H * scala) / 2.6;
      limita(); applica(); ok();
    };
    if (img.complete && img.naturalWidth) requestAnimationFrame(via); else img.addEventListener('load', () => requestAnimationFrame(via), { once: true });
  });
  const dita = new Map<number, { x: number; y: number }>();
  let inizio: { mx: number; my: number; dist: number; scala: number; tx: number; ty: number } | null = null;
  const stato = (): NonNullable<typeof inizio> => {
    const p = [...dita.values()];
    const mx = p.reduce((s, d) => s + d.x, 0) / p.length, my = p.reduce((s, d) => s + d.y, 0) / p.length;
    return { mx, my, dist: p.length > 1 ? Math.hypot(p[0]!.x - p[1]!.x, p[0]!.y - p[1]!.y) : 0, scala, tx, ty };
  };
  const punto = (e: PointerEvent) => { const r = cornice.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  cornice.addEventListener('pointerdown', (e) => { cornice.setPointerCapture(e.pointerId); dita.set(e.pointerId, punto(e)); inizio = stato(); });
  cornice.addEventListener('pointermove', (e) => {
    if (!dita.has(e.pointerId) || !inizio) return;
    dita.set(e.pointerId, punto(e));
    const ora = stato();
    if (dita.size > 1 && inizio.dist > 0) {
      const s = Math.max(minimo, Math.min(minimo * 6, inizio.scala * (ora.dist / inizio.dist)));
      tx = ora.mx - ((inizio.mx - inizio.tx) / inizio.scala) * s; ty = ora.my - ((inizio.my - inizio.ty) / inizio.scala) * s; scala = s;
    } else { tx = inizio.tx + ora.mx - inizio.mx; ty = inizio.ty + ora.my - inizio.my; }
    limita(); applica();
  });
  const lascia = (e: PointerEvent): void => { dita.delete(e.pointerId); inizio = dita.size ? stato() : null; };
  cornice.addEventListener('pointerup', lascia);
  cornice.addEventListener('pointercancel', lascia);
  cornice.addEventListener('keydown', (e) => {
    const passo = 24;
    const m: Record<string, [number, number]> = { ArrowLeft: [passo, 0], ArrowRight: [-passo, 0], ArrowUp: [0, passo], ArrowDown: [0, -passo] };
    if (m[e.key]) { e.preventDefault(); tx += m[e.key]![0]; ty += m[e.key]![1]; limita(); applica(); }
    else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoom(scala * 1.2); }
    else if (e.key === '-') { e.preventDefault(); zoom(scala / 1.2); }
  });
  const element = el('div', { class: 'ritaglio' }, cornice,
    el('div', { class: 'ritaglio-comandi' },
      el('button', { type: 'button', class: 'btn-secondario', 'aria-label': 'Allontana', click: () => zoom(scala / 1.25) }, '−'),
      el('button', { type: 'button', class: 'btn-secondario', 'aria-label': 'Avvicina il volto', click: () => zoom(scala * 1.25) }, '+')));
  return {
    element, pronto,
    ritaglio: () => ({ x: Math.max(0, -tx / (W * scala)), y: Math.max(0, -ty / (H * scala)), lato: Math.min(1, S / (W * scala)) }),
  };
}
